import React, { FC, ReactElement, useState } from 'react'
import { PartitionModal } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { Modal } from '../../../ReactRouting/Components/Molecules/Modal/Modal'
import { DocTopic } from '../../Common/Constants/Documentation'
import {
    LanguageContextProvider,
    MessageContextProvider,
    PromptContextProvider,
    ReactState
} from '../../Common/Contexts'
import { CommonFormContextProvider } from '../../Common/Contexts/CommonFormContext'
import { clearModalHelpTopic, setModalHelpTopicID } from '../../Common/Utils/HelpPortal'
import { PartitionConfigurationModalProps } from '../Types/Types'
import { PartitionTabs } from '../Main/PartitionTabs'
import { $window } from '../../../NonReact/common/AkixiWindow'
import '../Styles/PartitionConfiguration.scss'
import { isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'

/**
 * Modal wrapper for add/change/view partition configuration.
 *
 * @param props - Modal props.
 * @returns Partition configuration modal.
 */
export const PartitionConfigurationModal: FC<PartitionConfigurationModalProps> = ({
    refreshAction,
    filterTelSysID,
    selectedPartitionIDInTelSys,
    viewOnly,
    onModalExited
}: PartitionConfigurationModalProps): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(PartitionModal.NAME)

    const [isModalOpen, setModalOpen]: ReactState<boolean> = useState<boolean>(true)
    let modalTitle: string = language.get(PartitionModal.PartitionModal_AddTitle)
    if (viewOnly) {
        modalTitle = language.get(PartitionModal.PartitionModal_ViewTitle)
    } else if (isVarNotEmpty(selectedPartitionIDInTelSys)) {
        modalTitle = language.get(PartitionModal.PartitionModal_ChangeTitle)
    }

    const closeModal = (): void => {
        $window.disableHotKeys = false
        setModalOpen(false)
    }

    return (
        <div>
            <CommonFormContextProvider>
                <Modal
                    isOpen={isModalOpen}
                    isViewOnly={viewOnly}
                    closeOnBackdropClick={true}
                    isClosableWithXButton={true}
                    inlineExitPromptSettings={{
                        message: language.get(PartitionModal.PartitionModal_PromptMessage),
                        confirmButtonText: language.get(PartitionModal.PartitionModal_PromptConfirm),
                        cancelButtonText: language.get(PartitionModal.PartitionModal_PromptCancel),
                        isRouterMode: true
                    }}
                    onOpen={(): void => {
                        setModalHelpTopicID(DocTopic.ADMINPARTITIONSPROPS)
                    }}
                    onClose={closeModal}
                    onExited={(): void => {
                        clearModalHelpTopic()
                        onModalExited?.()
                    }}
                >
                    {isModalOpen ? (
                        <LanguageContextProvider>
                            <PromptContextProvider>
                                <MessageContextProvider>
                                    <div className='modal-title-report-config'>{modalTitle}</div>
                                    <div className='partition-configuration admin-modal-form'>
                                        <PartitionTabs
                                            closeModal={closeModal}
                                            refreshAction={refreshAction}
                                            filterTelSysID={filterTelSysID}
                                            selectedPartitionIDInTelSys={selectedPartitionIDInTelSys}
                                            viewOnly={viewOnly}
                                        />
                                    </div>
                                </MessageContextProvider>
                            </PromptContextProvider>
                        </LanguageContextProvider>
                    ) : (
                        <></>
                    )}
                </Modal>
            </CommonFormContextProvider>
        </div>
    )
}
